import { useState, useEffect, useCallback, memo } from "react";
import { getAllUsers } from "../api/usersApi";
import { toast } from "react-toastify";
import { Mail, Linkedin, Globe } from "lucide-react";

// Memoized ProfileCard for performance
const ProfileCard = memo(({ alumnus, index }) => {
  const [showSocials, setShowSocials] = useState(false);

  const getInitials = (name) => {
    if (!name) return "A";
    const parts = name.trim().split(" ");
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase();
  };

  const hasSocials = alumnus.email || alumnus.linkedinUrl || alumnus.websiteUrl;

  const handleCardClick = () => {
    if (window.innerWidth < 768) {
      // Only for mobile and tablet
      setShowSocials(!showSocials);
    }
  };

  return (
    <div
      className="relative w-full overflow-hidden rounded-3xl bg-white shadow-xl group cursor-pointer transform hover:scale-[1.03] transition-all duration-300 animate-fadein"
      style={{ animationDelay: `${Math.min(index * 0.1, 0.8)}s` }}
      onClick={handleCardClick}
    >
      {/* Top curved section */}
      <div className="absolute top-0 w-full h-48 bg-blue-600 rounded-b-[4rem]"></div>

      {/* Profile image container */}
      <div className="relative z-10 flex flex-col items-center pt-8">
        <div className="w-40 h-40 bg-white rounded-full flex items-center justify-center p-2">
          <div className="w-36 h-36 bg-white rounded-full border-4 border-blue-600 overflow-hidden">
            {alumnus.profileImageUrl ||
            alumnus.profileImage ||
            alumnus.image ? (
              <img
                src={
                  alumnus.profileImageUrl ||
                  alumnus.profileImage ||
                  alumnus.image
                }
                alt={alumnus.name || "Alumni Profile"}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.parentNode.querySelector(
                    ".alumni-initials"
                  ).style.display = "flex";
                }}
              />
            ) : (
              <div className="alumni-initials w-full h-full flex items-center justify-center bg-gray-300 text-gray-700 text-3xl font-bold">
                {getInitials(alumnus.name)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content section */}
      <div className="relative z-20 flex flex-col items-center text-center px-6 py-12">
        <h1 className="text-2xl font-semibold text-gray-800 mt-4">
          {alumnus.name || "Name not available"}
        </h1>
        <p className="mt-4 text-gray-600 leading-relaxed">
          {alumnus.designation && alumnus.companyOrInstitute
            ? `${alumnus.designation} at ${alumnus.companyOrInstitute}`
            : "Position not specified."}
        </p>

        {/* Click to see more text for mobile/tablet - only show if has socials and not showing socials yet */}
        {hasSocials && !showSocials && (
          <p className="mt-6 text-sm text-blue-600 font-medium md:hidden animate-pulse">
            👆 Click to see contact info
          </p>
        )}

        {/* Click to hide text for mobile/tablet - only show if socials are visible */}
        {hasSocials && showSocials && (
          <p className="mt-6 text-sm text-gray-500 font-medium md:hidden">
            👆 Click again to hide
          </p>
        )}

        {/* Social media links container */}
        {hasSocials && (
          <div
            className={`absolute bottom-4 flex flex-row items-center space-x-4 transition-all duration-500 z-30 
            ${
              showSocials
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            } 
            md:opacity-0 md:translate-y-10 md:group-hover:opacity-100 md:group-hover:translate-y-0`}
          >
            {alumnus.email && (
              <a
                href={`mailto:${alumnus.email}`}
                className="bg-blue-600 hover:bg-blue-500 text-white w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all duration-300"
                onClick={(e) => e.stopPropagation()}
              >
                <Mail className="w-6 h-6" />
              </a>
            )}
            {alumnus.linkedinUrl && (
              <a
                href={alumnus.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-700 hover:bg-blue-600 text-white w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all duration-300"
                onClick={(e) => e.stopPropagation()}
              >
                <Linkedin className="w-6 h-6" />
              </a>
            )}
            {alumnus.websiteUrl && (
              <a
                href={alumnus.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-600 hover:bg-gray-500 text-white w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all duration-300"
                onClick={(e) => e.stopPropagation()}
              >
                <Globe className="w-6 h-6" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

const AlumniProfile = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBatch, setFilterBatch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const filterAlumni = useCallback((alumni, search, batch) => {
    return alumni.filter((alumnus) => {
      const matchesSearch =
        !search || alumnus.name?.toLowerCase().includes(search.toLowerCase());
      // ||
      // alumnus.designation?.toLowerCase().includes(search.toLowerCase()) ||
      // alumnus.company?.toLowerCase().includes(search.toLowerCase());

      // const matchesBatch = !batch || alumnus.graduationYear === batch;
      const matchesBatch = !batch || alumnus.batch === batch;

      return matchesSearch && matchesBatch;
    });
  }, []);

  useEffect(() => setCurrentPage(1), [searchTerm, filterBatch]);

  useEffect(() => {
    fetchAlumni();
  }, []);

  const fetchAlumni = async () => {
    try {
      setLoading(true);
      const data = await getAllUsers();
      setAlumni(Array.isArray(data) ? data : data.users || []);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch alumni data");
      toast.error("Failed to load alumni profiles");
    } finally {
      setLoading(false);
    }
  };

  const filteredAlumni = filterAlumni(alumni, searchTerm, filterBatch);
  const totalPages = Math.ceil(filteredAlumni.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAlumni.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // const uniqueBatches = [
  //   ...new Set(alumni.map((a) => a.graduationYear).filter(Boolean)),
  // ].sort((a, b) => b - a);

  const uniqueBatches = [
    ...new Set(alumni.map((a) => a.batch).filter(Boolean)),
  ].sort((a, b) => b - a);

  const clearFilters = () => {
    setSearchTerm("");
    setFilterBatch("");
    setCurrentPage(1);
  };

  const renderContent = () => {
    if (loading)
      return (
        <div className="text-center text-white py-8 animate-pulse">
          Loading alumni profiles...
        </div>
      );
    if (error)
      return (
        <div className="text-center text-red-500 py-8">
          {error}
          <div className="mt-4">
            <button
              onClick={fetchAlumni}
              className="bg-amber-500 hover:bg-amber-600 text-black px-6 py-2 rounded-full shadow-md"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    if (filteredAlumni.length === 0)
      return (
        <div className="text-center text-gray-300 py-8">
          No alumni found matching your criteria.
          <div className="mt-4">
            <button
              onClick={clearFilters}
              className="bg-amber-500 hover:bg-amber-600 text-black px-6 py-2 rounded-full shadow-md"
            >
              Clear All Filters
            </button>
          </div>
        </div>
      );

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {currentItems.map((alumnus, index) => (
            <ProfileCard
              key={alumnus.id || index}
              alumnus={alumnus}
              index={index}
            />
          ))}
        </div>

        {/* Pagination */}
        {filteredAlumni.length > itemsPerPage && (
          <div className="flex justify-center items-center gap-2 mb-16 select-none">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              className="flex items-center px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-black font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <div className="flex items-center gap-1 px-4 py-2 rounded-full bg-slate-700 shadow border border-slate-600">
              <span className="text-white font-bold">{currentPage}</span>
              <span className="mx-1 text-gray-300 font-semibold">/</span>
              <span className="text-white font-bold">{totalPages}</span>
            </div>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              className="flex items-center px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-black font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-800" id="profile">
      <div className="max-w-7xl mx-auto">
        {/* Search and Filters */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
            <div className="md:col-span-5">
              <input
                type="text"
                placeholder="Search by Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-4 pr-4 py-2 rounded-full bg-slate-700 text-white placeholder-gray-400 border border-slate-600 shadow-md focus:ring-2 focus:ring-amber-500 focus:border-amber-400 transition-all duration-200"
              />
            </div>
            <div className="md:col-span-3">
              <select
                value={filterBatch}
                onChange={(e) => setFilterBatch(e.target.value)}
                className="w-full pl-4 pr-10 py-2 rounded-full bg-slate-700 text-white border border-slate-600 shadow-md focus:ring-2 focus:ring-amber-500 transition-all duration-200 cursor-pointer appearance-none"
              >
                <option value="">Graduation Year</option>
                {uniqueBatches.map((batch) => (
                  <option key={batch} value={batch}>
                    {batch}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {(searchTerm || filterBatch) && (
            <div className="mt-4 flex justify-center">
              <button
                onClick={clearFilters}
                className="bg-amber-500 hover:bg-amber-600 text-black px-6 py-2 rounded-full shadow-md"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Alumni Cards */}
        {renderContent()}
      </div>
    </section>
  );
};

export default AlumniProfile;
